import { useCallback, useEffect, useRef, useState } from "react";
import {
  getWholesaleToken,
  parseWholesaleLoginFromSearch,
  setWholesaleToken,
  wholesaleAuthHeaders,
  wholesaleCatalogFetch,
} from "../utils/catalogWholesale";

const STOREFRONT_TOKEN_KEY = "storefront_token";

/** Products and prices are fetched once per page load, so switching retail ↔ wholesale needs a reload. */
function reloadForPriceMode() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    ["ws", "ws_login", "username"].forEach((k) => url.searchParams.delete(k));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

export function useCatalogWholesaleSession({ storeKey, catalogSlug, cart, setCart, canEdit }) {
  const [wholesaleDealer, setWholesaleDealer] = useState(null);
  const [wholesaleCatalogEnabled, setWholesaleCatalogEnabled] = useState(false);
  const [wholesaleMode, setWholesaleMode] = useState(false);
  const [wholesaleAuthOpen, setWholesaleAuthOpen] = useState(false);
  const [wholesaleOrdersOpen, setWholesaleOrdersOpen] = useState(false);
  const [wholesaleEditingOrderId, setWholesaleEditingOrderId] = useState(null);
  const [wholesaleLoginUsername, setWholesaleLoginUsername] = useState("");
  const [wholesaleLoginUsernameLocked, setWholesaleLoginUsernameLocked] = useState(false);
  const didLoadWholesaleCartRef = useRef(false);
  const wholesaleCartTimerRef = useRef(null);
  const loginParamAppliedRef = useRef(false);

  const slug = (catalogSlug || storeKey || "").trim();

  const wholesaleCatalogHeaders = useCallback(
    (extra = {}) => wholesaleAuthHeaders(storeKey, extra, slug),
    [storeKey, slug]
  );

  const wholesaleLogout = useCallback(() => {
    setWholesaleToken(storeKey, null, slug);
    setWholesaleDealer(null);
    setWholesaleMode(false);
    setWholesaleEditingOrderId(null);
    didLoadWholesaleCartRef.current = false;
    reloadForPriceMode();
  }, [storeKey, slug]);

  const loadWholesaleSession = useCallback(async () => {
    if (!storeKey || canEdit) return;
    const token = getWholesaleToken(storeKey, slug);
    if (!token) {
      setWholesaleDealer(null);
      setWholesaleMode(false);
      didLoadWholesaleCartRef.current = false;
      return;
    }
    try {
      const meResp = await wholesaleCatalogFetch(storeKey, "/wholesale/me", {}, slug);
      if (meResp.status === 401) {
        setWholesaleToken(storeKey, null, slug);
        setWholesaleDealer(null);
        setWholesaleMode(false);
        didLoadWholesaleCartRef.current = false;
        return;
      }
      if (!meResp.ok) return;
      const me = await meResp.json().catch(() => ({}));
      setWholesaleDealer(me?.dealer || null);
      setWholesaleMode(true);
      try {
        localStorage.removeItem(STOREFRONT_TOKEN_KEY);
      } catch {
        /* ignore */
      }

      const cartResp = await wholesaleCatalogFetch(storeKey, "/wholesale/cart", {}, slug);
      if (cartResp.ok) {
        const data = await cartResp.json().catch(() => ({}));
        const items = Array.isArray(data.items) ? data.items : [];
        setCart(items);
        setWholesaleEditingOrderId(data.editing_order_id || null);
        didLoadWholesaleCartRef.current = true;
      }
    } catch {
      /* keep token on transient errors */
    }
  }, [storeKey, slug, canEdit, setCart]);

  useEffect(() => {
    if (!storeKey || canEdit) return;
    const token = getWholesaleToken(storeKey, slug);
    if (token) setWholesaleToken(storeKey, token, slug);
  }, [storeKey, slug, canEdit]);

  useEffect(() => {
    loadWholesaleSession();
  }, [loadWholesaleSession]);

  useEffect(() => {
    if (!storeKey || canEdit || loginParamAppliedRef.current) return;
    if (typeof window === "undefined") return;
    const parsed = parseWholesaleLoginFromSearch(window.location.search);
    if (!parsed.open) return;
    loginParamAppliedRef.current = true;
    if (parsed.username) {
      setWholesaleLoginUsername(parsed.username);
      setWholesaleLoginUsernameLocked(true);
    } else {
      setWholesaleLoginUsername("");
      setWholesaleLoginUsernameLocked(false);
    }
    if (!getWholesaleToken(storeKey, slug)) {
      setWholesaleAuthOpen(true);
    }
  }, [storeKey, slug, canEdit]);

  useEffect(() => {
    if (!storeKey || canEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await wholesaleCatalogFetch(storeKey, "/wholesale/info", {}, slug);
        if (!res.ok || cancelled) return;
        const data = await res.json().catch(() => ({}));
        if (!cancelled && data?.wholesale_catalog_enabled) {
          setWholesaleCatalogEnabled(true);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeKey, slug, canEdit]);

  useEffect(() => {
    if (!wholesaleDealer || !storeKey) return;
    if (!didLoadWholesaleCartRef.current) return;
    if (wholesaleCartTimerRef.current) window.clearTimeout(wholesaleCartTimerRef.current);
    wholesaleCartTimerRef.current = window.setTimeout(async () => {
      try {
        await wholesaleCatalogFetch(
          storeKey,
          "/wholesale/cart",
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items: cart || [],
              editing_order_id: wholesaleEditingOrderId,
            }),
          },
          slug
        );
      } catch {
        /* ignore */
      }
    }, 700);
    return () => {
      if (wholesaleCartTimerRef.current) window.clearTimeout(wholesaleCartTimerRef.current);
    };
  }, [cart, wholesaleDealer, storeKey, slug, wholesaleEditingOrderId]);

  const handleWholesaleLoginSuccess = useCallback(
    (dealer, token) => {
      if (token) setWholesaleToken(storeKey, token, slug);
      setWholesaleDealer(dealer);
      setWholesaleMode(true);
      try {
        localStorage.removeItem(STOREFRONT_TOKEN_KEY);
      } catch {
        /* ignore */
      }
      if (token) {
        reloadForPriceMode();
        return;
      }
      loadWholesaleSession();
    },
    [loadWholesaleSession, storeKey, slug]
  );

  const handleWholesaleEditOrder = useCallback(
    (items, orderId) => {
      setCart(Array.isArray(items) ? items : []);
      setWholesaleEditingOrderId(orderId || null);
      didLoadWholesaleCartRef.current = true;
    },
    [setCart]
  );

  const applyWholesaleBrandingFlags = useCallback((branding) => {
    if (!branding) return;
    if (branding.wholesale_catalog_enabled != null) {
      setWholesaleCatalogEnabled(!!branding.wholesale_catalog_enabled);
    }
    if (branding.wholesale_mode != null) {
      setWholesaleMode(!!branding.wholesale_mode);
    }
  }, []);

  return {
    wholesaleDealer,
    wholesaleCatalogEnabled,
    wholesaleMode,
    wholesaleAuthOpen,
    setWholesaleAuthOpen,
    wholesaleOrdersOpen,
    setWholesaleOrdersOpen,
    wholesaleEditingOrderId,
    setWholesaleEditingOrderId,
    wholesaleCatalogHeaders,
    wholesaleLogout,
    handleWholesaleLoginSuccess,
    handleWholesaleEditOrder,
    applyWholesaleBrandingFlags,
    wholesaleCatalogSlug: slug,
    wholesaleLoginUsername,
    wholesaleLoginUsernameLocked,
    didLoadWholesaleCartRef,
  };
}
